import React, { useEffect, useState } from 'react';
import { PackageOpen, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { justoAPI, blobErrorDetail } from '@/lib/justo';
import { downloadBlob, filenameFromHeaders } from '@/lib/recuentos';
import { locationsAPI } from '@/lib/api';
import { DateField } from '../recuentos/DateField';
import { EnviosTable } from './EnviosTable';

const ZipErrores = ({ errores }) => (
  <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="justo-zip-errores">
    <p className="font-semibold mb-1">No se generó el .zip:</p>
    <ul className="list-disc pl-5">{errores.map((e) => (
      <li key={e.message}>{e.message}{e.faltantes?.length ? `: ${e.faltantes.map((f) => f.codigo).join(', ')}` : ''}</li>
    ))}</ul>
  </div>
);

export const EnviosTab = () => {
  const [f, setF] = useState({ desde: '', hasta: '', local: 'all', solo: true });
  const [locales, setLocales] = useState([]);
  const [envios, setEnvios] = useState([]);
  const [sel, setSel] = useState({});
  const [errores, setErrores] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { locationsAPI.getAll(true).then((r) => setLocales(r.data)); }, []);
  const load = async () => {
    const params = { solo_exportables: f.solo };
    if (f.desde) params.fecha_desde = f.desde;
    if (f.hasta) params.fecha_hasta = f.hasta;
    if (f.local !== 'all') params.location_id = f.local;
    setEnvios((await justoAPI.envios(params)).data);
    setSel({});
  };
  useEffect(() => { load(); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (e) => setSel((s) => { const n = { ...s }; if (n[e.id]) delete n[e.id]; else n[e.id] = e; return n; });
  const toggleAll = (ev) => setSel(ev.target.checked ? Object.fromEntries(envios.filter((e) => e.exportable).map((e) => [e.id, e])) : {});

  const exportarZip = async () => {
    const refs = Object.values(sel).map((e) => ({ tipo: e.tipo, id: e.id }));
    const subidos = Object.values(sel).filter((e) => e.estado_justo === 'subido_justo').length;
    if (subidos && !window.confirm(`${subidos} de los envíos seleccionados ya fueron subidos a Justo. ¿Exportar igual?`)) return;
    setBusy(true); setErrores(null);
    try {
      const res = await justoAPI.exportarZip(refs);
      downloadBlob(res.data, filenameFromHeaders(res.headers, 'transferencias_justo.zip'));
      toast.success(`${refs.length} transferencias exportadas`);
      load();
    } catch (e) {
      const d = await blobErrorDetail(e);
      if (d?.errores) setErrores(d.errores); else toast.error(typeof d === 'string' ? d : 'Error al exportar');
    } finally { setBusy(false); }
  };

  const n = Object.keys(sel).length;
  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap gap-2 items-center">
        <DateField value={f.desde} onChange={(v) => setF({ ...f, desde: v })} placeholder="Desde" testId="justo-filtro-desde" />
        <DateField value={f.hasta} onChange={(v) => setF({ ...f, hasta: v })} placeholder="Hasta" testId="justo-filtro-hasta" />
        <Select value={f.local} onValueChange={(v) => setF({ ...f, local: v })}>
          <SelectTrigger className="w-52" data-testid="justo-filtro-local"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los locales</SelectItem>
            {locales.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={f.solo} onChange={(e) => setF({ ...f, solo: e.target.checked })} className="w-4 h-4 accent-orange-500" data-testid="justo-filtro-solo-exportables" /> Solo exportables
        </label>
        <Button variant="outline" onClick={load} data-testid="justo-envios-refresh"><RefreshCw className="w-4 h-4" /></Button>
        <Button onClick={exportarZip} disabled={!n || busy} className="ml-auto bg-green-600 hover:bg-green-700" data-testid="justo-exportar-zip-btn">
          <PackageOpen className="w-4 h-4 mr-1" /> Exportar {n || ''} seleccionados (.zip)
        </Button>
      </div>
      <p className="text-xs text-slate-400">Sin filtro de fecha se muestran los últimos 30 días.</p>
      {errores && <ZipErrores errores={errores} />}
      <EnviosTable envios={envios} onChanged={load} selectable selected={sel} onToggle={toggle} onToggleAll={toggleAll} />
    </div>
  );
};
